import { createSlice } from "@reduxjs/toolkit";
import { authApi } from "./auth.api";
import { schoolAuthApi } from "./school.auth.api";
import { getRoleName } from "../../shared/utils/roleMapping";
import { CONFIG_VARS } from "../../constants/urlConstants/configuration";

const ALLOWED_ROLES = Object.freeze([
  "Super Admin",
  "MIS Team Admin",
  "General Manager",
  "Senior Manager",
  "Manager",
  "Execution Rep",
  "School Admin",
]);

export const USER_TYPES = Object.freeze({
  SCHOOL: "school",
  SUPERADMIN: "super_admin",
});

const initialState = {
  user: null,
  tokens: null,
  mdm: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    userLogout(state) {
      state.user = initialState.user;
      state.tokens = initialState.tokens;
      state.mdm = initialState.mdm;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        authApi.endpoints.superAdminLogin.matchFulfilled,
        (state, { payload, meta }) => {
          if (payload && payload.roles) {
            const loginType = meta.arg.originalArgs.body.login_type;
            let allowedRoleFound = false;
            let userRoleName = null;

            for (const roleId of payload.roles) {
              userRoleName = getRoleName(roleId);
              if (userRoleName && ALLOWED_ROLES.includes(userRoleName)) {
                if (
                  (userRoleName === "School Admin" &&
                    loginType === USER_TYPES.SCHOOL) ||
                  (userRoleName !== "School Admin" &&
                    loginType !== USER_TYPES.SCHOOL)
                ) {
                  allowedRoleFound = true;
                  break;
                }
              }
            }

            if (allowedRoleFound) {
              state.user = {
                first_name: payload.firstName,
                last_name: payload.lastName,
                admission_id: payload.admission_number,
                crnId: payload.crnId,
              };

              state.tokens = {
                access_token: payload.accessToken,
                refresh_token: payload.refreshToken,
              };

              state.mdm = {
                role_ids: payload.roles,
              };
            }
          }
        }
      )
      .addMatcher(
        schoolAuthApi.endpoints.schoolAdminLogin.matchFulfilled,
        (state, { payload }) => {
          if (!payload) return;

          if (payload?.detail) {
            return;
          }

          const rawAccess = payload?.token?.access_token || "";
          const accessToken = rawAccess.replace(/^Bearer\s+/i, "");
          const refreshToken = payload?.token?.refresh_token || null;

          state.tokens = {
            access_token: accessToken,
            refresh_token: refreshToken,
          };

          const user = payload?.user || {};
          state.user = {
            first_name: user.first_name || "",
            last_name: user.last_name || null,
            admission_id: user.phone || "",
            crnId: state.user?.crnId || "",
            school_id: user.school_id || "",
            account_type: user.account_type || "",
            isd_code: user.isd_code || "",
            phone: user.phone || "",
            email: user.email || "",
          };

          const schoolAdminRoleId = Object.entries(
            CONFIG_VARS.roleMappings[1]
          ).find(([, value]) => value === "School Admin")?.[0];
          state.mdm = {
            role_ids: [schoolAdminRoleId],
          };
        }
      )
      // Handle verification responses
      .addMatcher(
        authApi.endpoints.verifyToken.matchRejected,
        (state, { error }) => {
          if (
            error?.status === 401 ||
            error?.data?.detail === "Expired Signature"
          ) {
            state.user = initialState.user;
            state.tokens = initialState.tokens;
            state.mdm = initialState.mdm;
          }
        }
      );
  },
});

export const { userLogout } = authSlice.actions;
export default authSlice.reducer;
